import React, { useEffect, useState } from 'react';
import { ArrowLeft, Check, CircleDot, FileText, RefreshCw, ShieldCheck } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';
import SessionGate from './SessionGate';
import { engineApiClient, EngineApiError } from '../shared/api/engineApiClient';

const DocumentWorkspaceContent: React.FC = () => {
  const { documentId = '' } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const { data, error, isLoading, mutate } = useSWR(
    documentId ? ['engine:document-bootstrap', documentId] : null,
    () => engineApiClient.getDocumentBootstrap(documentId),
    { revalidateOnFocus: false },
  );

  useEffect(() => {
    if (data?.document.title) setTitle(data.document.title);
  }, [data?.document.title]);

  const canRename = Boolean(data?.capabilities.allow.includes('document.update_metadata'));

  const saveTitle = async (event?: React.SyntheticEvent) => {
    event?.preventDefault();
    const normalized = title.trim();
    if (!data || !canRename || !normalized || normalized === data.document.title) return;
    setSaveState('saving');
    try {
      const updated = await engineApiClient.updateDocumentTitle(documentId, normalized, data.etag);
      await mutate({ ...data, document: updated.document, etag: updated.etag }, false);
      setSaveState('saved');
      window.setTimeout(() => setSaveState('idle'), 1800);
    } catch (requestError) {
      setSaveState('error');
      if (requestError instanceof EngineApiError && requestError.currentEtag) {
        await mutate();
      }
    }
  };

  if (isLoading) {
    return <div className="engine-app-workspace-skeleton" aria-label="正在打开文档" />;
  }

  if (error || !data) {
    return (
      <div className="engine-app-error-state engine-app-workspace-error" role="alert">
        <strong>{error instanceof EngineApiError && error.status === 404 ? '找不到这份文档' : '文档暂时无法打开'}</strong>
        <p>{error instanceof EngineApiError && error.requestId ? `请求编号：${error.requestId}` : '它可能已被删除，或你没有访问权限。'}</p>
        <button type="button" className="engine-app-secondary-button" onClick={() => navigate('/documents')}>
          <ArrowLeft size={15} />返回文档列表
        </button>
      </div>
    );
  }

  return (
    <div className="engine-app-workspace">
      <div className="engine-app-workspace-topbar">
        <button type="button" className="engine-app-icon-button" onClick={() => navigate('/documents')} aria-label="返回文档列表">
          <ArrowLeft size={18} />
        </button>
        <form onSubmit={saveTitle} className="engine-app-title-form">
          <input
            aria-label="文档标题"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            onBlur={(event) => void saveTitle(event)}
            readOnly={!canRename}
            maxLength={200}
          />
          <span aria-live="polite">
            {saveState === 'saving' ? <><RefreshCw className="engine-app-spin" size={13} />正在保存</> : null}
            {saveState === 'saved' ? <><Check size={13} />已保存</> : null}
            {saveState === 'error' ? '保存失败，已同步最新版本' : null}
          </span>
        </form>
        <span className={`engine-app-status is-${data.document.status}`}>{data.document.status === 'draft' ? '草稿' : '审核流程'}</span>
      </div>

      <div className="engine-app-workspace-grid">
        <aside className="engine-app-outline-panel">
          <p className="engine-app-panel-label">文档结构</p>
          <button type="button" className="is-active"><FileText size={15} />交付说明</button>
          <button type="button" disabled><CircleDot size={14} />内容区块</button>
        </aside>

        <section className="engine-app-canvas">
          <div className="engine-app-canvas-paper">
            <div className="engine-app-canvas-kicker">交付说明</div>
            <h1>{data.document.title}</h1>
            <p>在这里组织脚本、静帧与视频，让每次反馈都指向明确的内容和版本。</p>
            <div className="engine-app-canvas-placeholder">
              <span><FileText size={22} /></span>
              <strong>文档已连接到云端</strong>
              <p>基础信息会持久保存。内容编辑和多人协作将在下一阶段接入。</p>
            </div>
          </div>
        </section>

        <aside className="engine-app-context-panel">
          <p className="engine-app-panel-label">访问状态</p>
          <div className="engine-app-access-summary">
            <ShieldCheck size={18} />
            <span><strong>{data.accessContext.role}</strong><small>{data.capabilities.constraints.view === 'DRAFT' ? '可查看当前草稿' : '仅查看已提交内容'}</small></span>
          </div>
          <dl>
            <div><dt>版本</dt><dd>R{data.document.revision}</dd></div>
            <div><dt>协作状态</dt><dd>{data.draft ? '草稿已就绪' : '只读审核'}</dd></div>
            <div><dt>审核轮次</dt><dd>{data.visibleReviewRounds.length}</dd></div>
          </dl>
        </aside>
      </div>
    </div>
  );
};

const DocumentWorkspacePage: React.FC = () => (
  <SessionGate>
    <DocumentWorkspaceContent />
  </SessionGate>
);

export default DocumentWorkspacePage;
